import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ApiHttpService } from '@core/services/services/api-http.service';
import { ResourceService } from '@core/services/resource/resource.service';
import { SuccessResponseType } from '@core/types/http/respone.type';
import { SettingsSelectors } from '@store/settings';
import { filter, map, take } from 'rxjs/operators';
import { ChatwootContextMapperService } from './chatwoot-context-mapper.service';
import { CHATWOOT_CONTACT_LEVEL_KEYS, ChatwootContext } from '@shared/types/chatwoot-context';
import { environment } from '@env/environment';

declare global {
    interface Window {
        chatwootSettings?: {
            hideMessageBubble?: boolean;
            position?: 'left' | 'right';
            locale?: string;
            type?: 'standard' | 'expanded_bubble';
            darkMode?: 'auto' | 'light';
        };
        chatwootSDK?: {
            run: (config: { websiteToken: string; baseUrl: string }) => void;
        };
        $chatwoot?: {
            setUser: (identifier: string, userData: ChatwootUserData) => void;
            setCustomAttributes: (attributes: Record<string, string | number | boolean>) => void;
            setConversationCustomAttributes: (attributes: Record<string, string | number | boolean>) => void;
            deleteCustomAttribute: (key: string) => void;
            reset: () => void;
            toggle: (state?: 'open' | 'close') => void;
            setLocale: (locale: string) => void;
            isLoaded: boolean;
        };
    }
}

interface ChatwootUserData {
    email: string;
    name: string;
    avatar_url?: string;
    phone_number?: string;
    identifier_hash: string;
}

interface ChatwootIdentityResponse {
    identifier_hash: string;
    identifier: string;
    name: string;
    email: string;
    avatar_url: string | null;
    phone_number: string | null;
    organisation_name: string | null;
    website_token: string | null;
    base_url: string;
}

@Injectable({
    providedIn: 'root'
})
export class ChatwootService extends ResourceService {
    readonly isReady = signal(false);
    private readonly initialized = signal(false);
    private readonly scriptLoaded = signal(false);

    private readonly router = inject(Router);
    private readonly settingsSelectors = inject(SettingsSelectors);
    private readonly destroyRef = inject(DestroyRef);
    private readonly contextMapper = inject(ChatwootContextMapperService);
    private contextSubscriptionStarted = false;

    constructor(private httpService: ApiHttpService) {
        super();
        this.trackPageNavigation();
        this.trackSelectedPartners();
    }

    initialize(): void {
        if (this.initialized()) {
            return;
        }

        this.fetchIdentityAndBoot();
    }

    reset(): void {
        if (window.$chatwoot?.isLoaded) {
            window.$chatwoot.reset();
        }

        this.initialized.set(false);
    }

    openChat(): void {
        if (environment.chatwoot?.structuredContext) {
            // Take the latest emitted context (if any) and push it before opening.
            this.contextMapper.context$.pipe(take(1)).subscribe(ctx => {
                this.setStructuredContext(ctx);
                window.$chatwoot?.toggle('open');
            });

            return;
        }

        // Legacy path
        const attributes: Record<string, string> = {
            initiated_from: window.location.href
        };

        this.settingsSelectors.selectedPartners$.pipe(take(1)).subscribe(partners => {
            attributes['partner'] = this.formatPartnerNames(partners);
            window.$chatwoot?.setConversationCustomAttributes(attributes);
        });

        window.$chatwoot?.toggle('open');
    }

    closeChat(): void {
        window.$chatwoot?.toggle('close');
    }

    setStructuredContext(ctx: ChatwootContext): void {
        if (!window.$chatwoot?.isLoaded) {
            return;
        }

        const contactAttrs: Record<string, string | number | boolean> = {};
        const conversationAttrs: Record<string, string | number | boolean> = {};
        const contactKeys = new Set<string>(CHATWOOT_CONTACT_LEVEL_KEYS);

        for (const [key, value] of Object.entries(ctx)) {
            if (value === null || value === undefined) {
                continue;
            }

            if (contactKeys.has(key)) {
                contactAttrs[key] = value as string | number | boolean;
            } else {
                conversationAttrs[key] = value as string | number | boolean;
            }
        }

        if (Object.keys(contactAttrs).length > 0) {
            window.$chatwoot.setCustomAttributes(contactAttrs);
        }

        if (Object.keys(conversationAttrs).length > 0) {
            window.$chatwoot.setConversationCustomAttributes(conversationAttrs);
        }
    }

    private fetchIdentityAndBoot(): void {
        this.httpService
            .get<ChatwootIdentityResponse>(this.createUrl('chatwoot/identity-hash'))
            .pipe(map((response: SuccessResponseType<ChatwootIdentityResponse>) => response.data))
            .subscribe({
                next: identity => {
                    if (!identity.website_token || !identity.base_url) {
                        return;
                    }

                    this.bootWidget(identity);
                },
                error: () => {}
            });
    }

    private bootWidget(identity: ChatwootIdentityResponse): void {
        window.chatwootSettings = {
            hideMessageBubble: true,
            position: 'right',
            locale: 'en',
            type: 'standard',
            darkMode: 'light'
        };

        if (this.scriptLoaded()) {
            this.setUser(identity);
            this.setCustomAttributes(identity);
            this.initialized.set(true);
            this.isReady.set(true);

            return;
        }

        this.loadScript(identity.base_url, () => {
            this.scriptLoaded.set(true);

            window.chatwootSDK?.run({
                websiteToken: identity.website_token!,
                baseUrl: identity.base_url
            });

            window.addEventListener('chatwoot:ready', () => {
                this.setUser(identity);
                this.setCustomAttributes(identity);
                this.initialized.set(true);
                this.isReady.set(true);
            });
        });
    }

    private setUser(identity: ChatwootIdentityResponse): void {
        window.$chatwoot?.setUser(identity.identifier, {
            email: identity.email,
            name: identity.name,
            avatar_url: identity.avatar_url ?? undefined,
            phone_number: identity.phone_number ?? undefined,
            identifier_hash: identity.identifier_hash
        });
    }

    private setCustomAttributes(identity: ChatwootIdentityResponse): void {
        const attributes: Record<string, string> = {
            current_page: window.location.href
        };

        if (identity.organisation_name) {
            attributes['organisation'] = identity.organisation_name;
        }

        window.$chatwoot?.setCustomAttributes(attributes);

        this.settingsSelectors.selectedPartners$.pipe(take(1)).subscribe(partners => {
            window.$chatwoot?.setCustomAttributes({
                partner: this.formatPartnerNames(partners)
            });
        });
    }

    private trackPageNavigation(): void {
        if (!environment.chatwoot?.structuredContext) {
            // Legacy path — preserved for the one-release backward-compat window
            this.router.events
                .pipe(
                    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(event => {
                    if (!this.initialized() || !window.$chatwoot?.isLoaded) {
                        return;
                    }

                    const currentPage = window.location.origin + event.urlAfterRedirects;

                    window.$chatwoot.setCustomAttributes({ current_page: currentPage });
                    window.$chatwoot.setConversationCustomAttributes({ current_page: currentPage });
                });

            return;
        }

        if (this.contextSubscriptionStarted) {
            return;
        }

        this.contextSubscriptionStarted = true;
        this.contextMapper.start();

        this.contextMapper.context$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(ctx => {
            if (!this.initialized() || !window.$chatwoot?.isLoaded) {
                return;
            }

            this.setStructuredContext(ctx);
        });
    }

    private trackSelectedPartners(): void {
        if (environment.chatwoot?.structuredContext) {
            return; // Mapper handles partner changes via SettingsSelectors stream
        }

        this.settingsSelectors.selectedPartners$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(partners => {
            if (!this.initialized() || !window.$chatwoot?.isLoaded) {
                return;
            }

            window.$chatwoot.setCustomAttributes({
                partner: this.formatPartnerNames(partners)
            });
        });
    }

    private formatPartnerNames(partners: { display_name: string }[]): string {
        return partners.length ? partners.map(p => p.display_name).join(', ') : 'None';
    }

    private loadScript(baseUrl: string, onLoad: () => void): void {
        const script = document.createElement('script');

        script.src = `${baseUrl}/packs/js/sdk.js`;
        script.async = true;
        script.defer = true;
        script.onload = onLoad;
        document.head.appendChild(script);
    }
}
